import { Block, Document, Group } from "@/types/document";
import { TableOfContentNode } from "../side-panel/toc/tocService";

const WORDS_PER_MINUTE = 200;

/**
 * Calculate word count from blocks
 */
export const calculateWordCount = (blocks: Block[]): number => {
  return blocks.reduce((total, block) => {
    // Only count words in text blocks and headers
    if (block.type === "text" || block.type.startsWith("header")) {
      return total + block.value.trim().split(/\s+/).length;
    }
    return total;
  }, 0);
};

/**
 * Calculate estimated reading time in minutes
 */
export const calculateReadingTime = (wordCount: number): number => {
  return Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));
};

/**
 * Get word count and estimated reading time for a group
 */
export const getGroupMetadata = (group: Group, document: Document) => {
  // Get all blocks for this group
  const blocks = document.blocks.filter((block) =>
    group.blockIds.includes(block.id)
  );

  const wordCount = calculateWordCount(blocks);
  const estimatedTime = calculateReadingTime(wordCount);

  return {
    wordCount,
    estimatedTime,
  };
};

/**
 * Get word count and estimated reading time for entire document
 */
export const getDocumentMetadata = (document: Document) => {
  const wordCount = calculateWordCount(document.blocks);
  const estimatedTime = calculateReadingTime(wordCount);

  return {
    wordCount,
    estimatedTime,
    sectionTitle: undefined as string | undefined,
  };
};

/**
 * Find a TOC node by ID in the tree
 */
const findNodeById = (nodes: TableOfContentNode[], nodeId: string): TableOfContentNode | null => {
  for (const node of nodes) {
    if (node.id === nodeId) return node;
    const found = findNodeById(node.children, nodeId);
    if (found) return found;
  }
  return null;
};

/**
 * Collect all block IDs from a node and its children
 */
const collectBlockIdsFromNode = (node: TableOfContentNode, document: Document): string[] => {
  const blockIds: string[] = [];
  
  const collectRecursive = (currentNode: TableOfContentNode) => {
    // For grouped documents, we need to get blocks from the group
    if (currentNode.group) {
      // If this is a group node (level 1), get all blocks in the group
      if (currentNode.level === 1) {
        blockIds.push(...currentNode.group.blockIds);
      } else if (currentNode.block) {
        // For header nodes within a group, collect blocks from this header to the next header
        const groupBlocks = currentNode.group.blockIds;
        const headerIndex = groupBlocks.indexOf(currentNode.block.id);
        
        if (headerIndex !== -1) {
          // Start from this header
          blockIds.push(currentNode.block.id);
          
          // Find the next sibling or parent's sibling header at same or higher level
          // For now, collect until next header or end of group
          for (let i = headerIndex + 1; i < groupBlocks.length; i++) {
            const blockId = groupBlocks[i];
            const block = document.blocks.find(b => b.id === blockId);
            
            // Stop if we hit another header at same or higher level
            if (block && block.type.startsWith("header")) {
              const headerLevel = parseInt(block.type.replace("header", ""));
              const currentHeaderLevel = parseInt(currentNode.block.type.replace("header", ""));
              if (headerLevel <= currentHeaderLevel) {
                break;
              }
            }
            
            blockIds.push(blockId);
          }
        }
      }
    } else if (currentNode.block) {
      // For non-grouped documents, collect blocks from this header to the next header
      const blockIndex = document.blocks.findIndex(b => b.id === currentNode.block!.id);
      
      if (blockIndex !== -1) {
        // Start from this header
        blockIds.push(currentNode.block.id);
        
        // Find next header at same or higher level
        const currentLevel = parseInt(currentNode.block.type.replace("header", ""));
        
        for (let i = blockIndex + 1; i < document.blocks.length; i++) {
          const block = document.blocks[i];
          
          // Stop if we hit another header at same or higher level
          if (block.type.startsWith("header")) {
            const headerLevel = parseInt(block.type.replace("header", ""));
            if (headerLevel <= currentLevel) {
              break;
            }
          }
          
          blockIds.push(block.id);
        }
      }
    }
    
    // Recursively collect from children
    currentNode.children.forEach(child => collectRecursive(child));
  };
  
  collectRecursive(node);
  return blockIds;
};

/**
 * Get word count and estimated reading time for a specific TOC section including its children
 */
export const getSectionMetadata = (
  sectionId: string,
  tableOfContents: TableOfContentNode[],
  document: Document
) => {
  // Find the node
  const node = findNodeById(tableOfContents, sectionId);
  if (!node) {
    return getDocumentMetadata(document);
  }
  
  // Collect all block IDs from this node and its children
  const blockIds = collectBlockIdsFromNode(node, document);
  
  // Get the actual blocks
  const blocks = document.blocks.filter(block => blockIds.includes(block.id));
  
  const wordCount = calculateWordCount(blocks);
  const estimatedTime = calculateReadingTime(wordCount);
  
  return {
    wordCount,
    estimatedTime,
    sectionTitle: node.title,
  };
};


